<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import CloseDispositionModal from 'dashboard/components-next/ConversationWorkflow/CloseDispositionModal.vue';
import {
  isGreetingOnlyConversation,
  needsContactClassification,
  isFollowUpOutcome,
  GREETING_ONLY_REASON,
} from 'dashboard/helper/closeDisposition';

// Eltafouk: comment threads have no status lifecycle (no resolve / reopen /
// snooze / mark-pending) — they are triaged with تم / محتاج-رد labels. This flag
// (forwarded from ConversationHeader via MoreActions) hides those controls.
// Defaults false so every DM inbox/folder renders exactly as before.
defineProps({
  isCommentSection: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);
const closeDispositionModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = (
  status,
  snoozedUntil,
  customAttributes = null,
  closeDisposition = null,
  conversationId = null
) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    // The explicit id wins: with the resolve-and-next shortcut the list may have
    // already advanced past the conversation the modal was answered for.
    conversationId: conversationId || currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }
  if (closeDisposition) {
    payload.closeDisposition = closeDisposition;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
    isLoading.value = false;
  });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes,
      context.closeDisposition || null,
      context.id || null
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

// Eltafouk: the original resolve flow (upstream required-attributes gate then
// resolve), now carrying the close-disposition answers with it.
const proceedResolve = (closeDisposition = null, conversationId = null) => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: conversationId || currentChat.value.id,
      snoozedUntil: null,
      closeDisposition,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      null,
      null,
      closeDisposition,
      conversationId
    );
  }
};

const handleCloseDisposition = ({
  reason,
  note,
  outcome,
  classification,
  snoozedUntil,
  context,
}) => {
  const closeDisposition = { reason, note, outcome, classification };

  // «محتاجة متابعة» snoozes instead of resolving: the conversation comes back at the
  // time the agent picked in the modal, or the moment the customer writes — resolving
  // it would leave the follow-up depending on the customer to remember it for us.
  // The required-attributes gate is skipped on purpose: it guards resolving.
  if (isFollowUpOutcome(outcome) && snoozedUntil) {
    toggleStatus(
      wootConstants.STATUS_TYPE.SNOOZED,
      snoozedUntil,
      null,
      closeDisposition,
      context?.id
    );
    return;
  }

  proceedResolve(closeDisposition, context?.id);
};

const onCmdResolveConversation = () => {
  // While the modal is open, further resolve presses are ignored: replacing the
  // context would silently drop the earlier conversation's resolve.
  if (closeDispositionModalRef.value?.isOpen()) return;

  // Eltafouk: a customer who never typed anything (greeting-button taps only) has no
  // «سبب تواصل» to record — resolve directly, stamped «ترحيب فقط», no questions.
  if (isGreetingOnlyConversation(currentChat.value)) {
    proceedResolve({
      reason: GREETING_ONLY_REASON,
      note: null,
      outcome: null,
      classification: null,
    });
    return;
  }

  closeDispositionModalRef.value?.open(
    { id: currentChat.value.id },
    { askClassification: needsContactClassification(currentChat.value) }
  );
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div
    v-if="!isCommentSection"
    class="flex relative justify-end items-center resolve-actions"
  >
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <!-- Eltafouk: «حل المحادثة» أخضر مصمت — طلب المالك ٢٣/٨ «خليه جذاب».
           كان `slate` زيه زي أي زرار تاني في الشاشة، وهو **الفعل اللي بتنتهي
           بيه المحادثة**: الموظفة بتدوسه عشرات المرات في اليوم وهو أهم زرار
           عندها. `teal/solid` = `bg-n-teal-9 text-white` وبيدي تباين عالي في
           الثيمين، والأخضر هنا معناه «خلصت» مش زينة. -->
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        size="sm"
        color="teal"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <!-- 🚨 السهم ياخد نفس لون الزرار اللي جنبه، وإلا المجموعة تطلع نصها
           أخضر ونصها رمادي وتبان مكسورة. أخضر وقت «حل المحادثة» بس. -->
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        :color="isOpen ? 'teal' : 'slate'"
        trailing-icon
        @click="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <!-- 🚨 «تحديد كمعلق» اتشال عمدًا. الحالة دي في شات-ووت أصلاً للبوتات، وعندنا
             كانت حفرة: المحادثة المعلقة **مابترجعش بالوقت** (مفيش جوب بيراجعها)،
             و**مابترجعش حتى لو العميل بعت رسالة** (`Message#reopen_conversation`
             بيفتح المؤجلة والمحلولة بس)، وجوب «غير مقروء» بيتخطاها (`.open` فقط).
             يعني أي محادثة تدخلها ماحدش يشوفها تاني إلا بفلتر يدوي.
             البديل الصح للموظف = «غفوة» (بترجع بالوقت أو بأول رسالة من العميل). -->
      </WootDropdownMenu>
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <CloseDispositionModal
      ref="closeDispositionModalRef"
      @submit="handleCloseDisposition"
    />
  </div>
</template>
